import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  CheckCircle,
  Email,
  PersonAdd,
  ContactMail,
  ManageAccounts,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import api from '../../services/api';

// Club Agreement Version - Update this when the agreement changes
const CLUB_AGREEMENT_VERSION = '01-08-2025';

interface PasswordRequirement {
  test: (password: string) => boolean;
  message: string;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const passwordRequirements: PasswordRequirement[] = [
    { test: (p) => p.length >= 8, message: t('managerRegistration.passwordRequirements.minLength') },
    { test: (p) => /[A-Z]/.test(p), message: t('managerRegistration.passwordRequirements.uppercase') },
    { test: (p) => /[a-z]/.test(p), message: t('managerRegistration.passwordRequirements.lowercase') },
    { test: (p) => /[0-9]/.test(p), message: t('managerRegistration.passwordRequirements.number') },
    { test: (p) => /[@$!%*?&#_\-.]/.test(p), message: t('managerRegistration.passwordRequirements.special') },
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    repeatPassword: '',
    clubAgreementAccepted: false,
    privacyPolicyAccepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | React.ReactNode>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.name || !formData.email || !formData.password || !formData.repeatPassword) {
      setError(t('managerRegistration.errors.allFieldsRequired'));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('managerRegistration.errors.invalidEmail'));
      return false;
    }

    if (!passwordRequirements.every(req => req.test(formData.password))) {
      setError(t('managerRegistration.errors.passwordRequirements'));
      return false;
    }

    if (formData.password !== formData.repeatPassword) {
      setError(t('managerRegistration.errors.passwordMismatch'));
      return false;
    }

    if (!formData.clubAgreementAccepted) {
      setError(t('managerRegistration.errors.acceptTerms'));
      return false;
    }

    if (!formData.privacyPolicyAccepted) {
      setError(t('managerRegistration.errors.acceptPrivacy'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);
      
      // Note: This endpoint requires admin authentication in the backend
      // For a real self-registration, the backend would need a public endpoint
      const response = await api.post('/register-user', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        repeat_password: formData.repeatPassword,
        club_agreement_accepted: formData.clubAgreementAccepted,
        club_agreement_version: CLUB_AGREEMENT_VERSION,
        privacy_policy_accepted: formData.privacyPolicyAccepted,
        language: (i18n.language?.substring(0, 2) === 'it' ? 'it' : 'en') as 'en' | 'it',  // Extract language code, default to 'en'
      });
      
      if (response.data.user_id) {
        setRegistrationSuccess(true);
        setActiveStep(1);
      }
    } catch (err: any) {
      console.error('Registration error:', err.response);
      if (err.response?.status === 400 && err.response?.data?.detail === 'User with this email already exists') {
        // User already exists - show forgot password option
        setError(
          <>
            {t('managerRegistration.errors.userExists')}
            {' '}
            <Button 
              size="small" 
              onClick={() => navigate('/')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('managerRegistration.errors.goToLogin')}
            </Button>
            {' '}
            {t('managerRegistration.errors.forgotPassword')}
          </>
        );
      } else if (err.response?.status === 422 && err.response?.data?.detail) {
        // Validation error from FastAPI
        const validationErrors = err.response.data.detail;
        if (Array.isArray(validationErrors) && validationErrors.length > 0) {
          // Extract the error message from the validation error
          const firstError = validationErrors[0];
          if (firstError.msg) {
            setError(firstError.msg);
          } else {
            setError(t('managerRegistration.errors.validationError'));
          }
        } else {
          setError('Validation error: Please check your input');
        }
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        // Expected error - the endpoint requires admin auth
        setError(t('managerRegistration.errors.registrationFailed'));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError(t('managerRegistration.errors.contactAdmin'));
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      label: t('managerRegistration.steps.createAccount'),
      icon: <PersonAdd />,
    },
    {
      label: t('managerRegistration.steps.verifyEmail'),
      icon: <Email />,
    },
    {
      label: t('managerRegistration.steps.contactSupport'),
      icon: <ContactMail />,
    },
    {
      label: t('managerRegistration.steps.startManaging'),
      icon: <ManageAccounts />,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
        position: 'relative',
      }}
    >
        <Container maxWidth="lg">
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700, 
              mb: 2,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            {t('managerRegistration.pageTitle')}
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            {t('managerRegistration.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Registration Form */}
          <Paper 
            elevation={0}
            sx={{ 
              flex: 1, 
              p: 4,
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              {t('managerRegistration.form.title')}
            </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {registrationSuccess ? (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('managerRegistration.success.title')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('managerRegistration.success.message')}
              </Typography>
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('managerRegistration.form.username')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                required
                disabled={loading}
              />
              
              <TextField
                fullWidth
                label={t('managerRegistration.form.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
                disabled={loading}
              />
              
              <TextField
                fullWidth
                label={t('managerRegistration.form.password')}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                disabled={loading}
              />
              
              <TextField
                fullWidth
                label={t('managerRegistration.form.repeatPassword')}
                name="repeatPassword"
                type="password"
                value={formData.repeatPassword}
                onChange={handleChange}
                margin="normal"
                required
                disabled={loading}
              />

              {/* Password Requirements */}
              <Box 
                sx={{ 
                  mt: 2, 
                  mb: 3, 
                  p: 2, 
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderRadius: 1,
                  border: '1px solid rgba(25, 118, 210, 0.3)'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: 'text.primary' }}>
                  {t('managerRegistration.passwordRequirements.title')}
                </Typography>
                <List dense sx={{ pl: 2 }}>
                  {passwordRequirements.map((req, index) => (
                    <ListItem key={index} disablePadding sx={{ minHeight: 'auto', py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        {req.test(formData.password) ? (
                          <CheckCircle fontSize="small" sx={{ fontSize: 16, color: 'success.main' }} />
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={req.message}
                        primaryTypographyProps={{
                          variant: 'caption',
                          color: req.test(formData.password) ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>{t('managerRegistration.note')}</strong>
                </Typography>
              </Alert>

              {/* Legal Agreements - GDPR Compliant */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('managerRegistration.legalAgreements.title')}
                </Typography>
                
                {/* Terms and Conditions (bundled with Club Agreement) */}
                <FormControlLabel
                  control={
                    <Checkbox
                      name="clubAgreementAccepted"
                      checked={formData.clubAgreementAccepted}
                      onChange={handleChange}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('managerRegistration.legalAgreements.terms')}{' '}
                      <Link
                        href="https://www.hikeandfly.app/terms"
                        target="_blank"
                        sx={{ color: 'primary.main' }}
                      >
                        {t('managerRegistration.legalAgreements.termsLink')}
                      </Link>
                      {' '}{t('managerRegistration.legalAgreements.and')}{' '}
                      <Link
                        href="https://www.hikeandfly.app/club-agreement"
                        target="_blank"
                        sx={{ color: 'primary.main' }}
                      >
                        {t('managerRegistration.legalAgreements.clubAgreement')}
                      </Link>
                      {' '}({t('managerRegistration.legalAgreements.version')} {CLUB_AGREEMENT_VERSION})
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />
                
                {/* Privacy Policy - Must be separate per GDPR */}
                <FormControlLabel
                  control={
                    <Checkbox
                      name="privacyPolicyAccepted"
                      checked={formData.privacyPolicyAccepted}
                      onChange={handleChange}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('managerRegistration.legalAgreements.privacy')}{' '}
                      <Link
                        href="https://www.hikeandfly.app/privacy"
                        target="_blank"
                        sx={{ color: 'primary.main' }}
                      >
                        {t('managerRegistration.legalAgreements.privacyLink')}
                      </Link>
                      {' '}{t('managerRegistration.legalAgreements.dataProcessing')}
                    </Typography>
                  }
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                  disabled={loading}
                  fullWidth
                >
                  {t('managerRegistration.form.backToLogin')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || registrationSuccess}
                  fullWidth
                  sx={{
                    background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : t('managerRegistration.form.submit')}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Process Steps */}
        <Paper 
          elevation={0}
          sx={{ 
            flex: 1, 
            p: 4,
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            {t('managerRegistration.howItWorks')}
          </Typography>
          
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  optional={
                    index === 3 ? (
                      <Typography variant="caption">{t('managerRegistration.steps.finalStep')}</Typography>
                    ) : null
                  }
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: activeStep >= index ? 'primary.main' : 'grey.300',
                        color: 'white',
                      }}
                    >
                      {step.icon}
                    </Box>
                  )}
                >
                  {step.label}
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {index === 0 && t('managerRegistration.steps.step1Desc')}
                    {index === 1 && t('managerRegistration.steps.step2Desc')}
                    {index === 2 && t('managerRegistration.steps.step3Desc')}
                    {index === 3 && t('managerRegistration.steps.step4Desc')}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('managerRegistration.support.title')}
            </Typography>
            <Typography variant="body2">
              {t('managerRegistration.support.email')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('managerRegistration.support.note')}
            </Typography>
          </Box>
        </Paper>
      </Box>
        </Container>
    </Box>
  );
};

export default RegisterPage;